import React, { useState, useCallback } from 'react';
import { FileText, AlertCircle, Loader2, CheckCircle, XCircle, Info } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import * as pdfjsLib from 'pdfjs-dist';
import { getOpenAIInstance } from '../../lib/openai';

interface ContractAnalysis {
  riskLevel: 'low' | 'medium' | 'high';
  keyTerms: { term: string; explanation: string }[];
  obligations: string[];
  recommendations: string[];
  confidence: number;
}

export function SmartContractAnalyzer() {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<ContractAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const analyzeContract = async (text: string) => {
    const openai = await getOpenAIInstance();

    const completion = await openai.chat.completions.create({
      messages: [
        {
          role: 'system',
          content:
            'Analyze the contract text and provide key insights, risks, and recommendations.',
        },
        {
          role: 'user',
          content: `Analyze this contract: ${text}`,
        },
      ],
      model: 'gpt-4-turbo-preview',
      response_format: { type: 'json_object' },
    });

    return JSON.parse(completion.choices[0].message.content);
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setAnalyzing(true);
    setError(null);
    setProgress(0);

    try {
      const file = acceptedFiles[0];
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = '';
      const totalPages = pdf.numPages;

      for (let i = 1; i <= totalPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: any) => item.str).join(' ');
        fullText += pageText + ' ';

        // Update progress
        setProgress((i / totalPages) * 50); // First 50% for PDF processing
      }

      // Simple text preprocessing
      const words = fullText
        .toLowerCase()
        .replace(/[^\w\s]/g, ' ')
        .split(/\s+/)
        .filter(word => word.length > 0);

      setProgress(75); // 75% after text processing

      const analysis = await analyzeContract(words.join(' '));
      setAnalysis(analysis);
      setProgress(100);
    } catch (err) {
      console.error('Error analyzing contract:', err);
      setError('Failed to analyze contract. Please try again.');
    } finally {
      setAnalyzing(false);
      setProgress(0);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    disabled: analyzing,
  });

  return (
    <div className="bg-dark-card rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <FileText className="h-6 w-6 text-primary-400" />
          <h2 className="text-xl font-semibold text-dark-text">Contract Analyzer</h2>
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors relative
          ${isDragActive ? 'border-primary-400 bg-primary-400/10' : 'border-dark-border'}
          ${analyzing ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <input {...getInputProps()} />
        {analyzing ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 text-primary-400 animate-spin" />
            <p className="mt-4 text-dark-text">Analyzing contract...</p>
            {progress > 0 && (
              <div className="w-full max-w-xs mt-4">
                <div className="bg-dark-bg rounded-full h-2">
                  <div
                    className="bg-primary-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-dark-muted mt-2">{progress}% complete</p>
              </div>
            )}
          </div>
        ) : (
          <>
            <FileText className="h-12 w-12 text-dark-muted mx-auto mb-4" />
            <p className="text-dark-text font-medium">Drop contract here or click to upload</p>
            <p className="text-dark-muted text-sm mt-2">Supports PDF format</p>
          </>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center text-red-400">
            <AlertCircle className="h-5 w-5 mr-2" />
            <p>{error}</p>
          </div>
        </div>
      )}

      {analysis && (
        <div className="mt-6 space-y-6">
          <div className="bg-dark-bg rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-dark-text">Risk Assessment</h3>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium
                ${analysis.riskLevel === 'low' ? 'bg-green-400/10 text-green-400' : ''}
                ${analysis.riskLevel === 'medium' ? 'bg-yellow-400/10 text-yellow-400' : ''}
                ${analysis.riskLevel === 'high' ? 'bg-red-400/10 text-red-400' : ''}`}
              >
                {analysis.riskLevel.toUpperCase()} RISK
              </span>
            </div>
            <div className="mt-4">
              <p className="text-dark-muted">Confidence Score</p>
              <div className="mt-2">
                <div className="bg-dark-border rounded-full h-2">
                  <div
                    className="bg-primary-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${analysis.confidence * 100}%` }}
                  />
                </div>
                <p className="text-sm text-dark-text mt-1">
                  {(analysis.confidence * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-dark-bg rounded-lg p-4">
            <h3 className="text-lg font-medium text-dark-text mb-4">Key Terms</h3>
            <div className="space-y-3">
              {analysis.keyTerms.map((term, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Info className="h-5 w-5 text-primary-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-dark-text font-medium">{term.term}</p>
                    <p className="text-dark-muted text-sm">{term.explanation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-dark-bg rounded-lg p-4">
            <h3 className="text-lg font-medium text-dark-text mb-4">Obligations</h3>
            <div className="space-y-2">
              {analysis.obligations.map((obligation, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <p className="text-dark-muted">{obligation}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-dark-bg rounded-lg p-4">
            <h3 className="text-lg font-medium text-dark-text mb-4">Recommendations</h3>
            <div className="space-y-2">
              {analysis.recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Info className="h-5 w-5 text-primary-400 flex-shrink-0 mt-1" />
                  <p className="text-dark-muted">{recommendation}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
